import {
  Button,
  ButtonVariant,
  EmptyState,
  EmptyStateBody,
  EmptyStateIcon,
  EmptyStateSecondaryActions,
  Pagination,
  Title,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
  ToolbarToggleGroup,
} from '@patternfly/react-core'
import { FilterIcon, SearchIcon, SortIcon, SyncAltIcon } from '@patternfly/react-icons'
import * as React from 'react'
import { useCallback, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { NavLink } from 'react-router-dom'

import { useStartInteraction, useUpdateVisitStatus } from '@app/domain/visit/visit.hooks'
import type {
  IVisit
} from '@app/domain/visit/visit.interface'
import {
  VisitStatusId,
} from '@app/domain/visit/visit.interface'
import type { IPagination } from '@app/lib/pagination'
import { useHandleCurrentPageChanged } from '@app/lib/pagination'
import type { ISortDirection, ISorting } from '@app/lib/sorting'
import type { AppState } from '@app/state'
import type {
  State as VisitListState
} from '@app/state/visit-list/state'
import {
  actions as VisitListActions,
} from '@app/state/visit-list/state'

import { LaunchIntakeModal } from './list/launch_intake_modal'
import { VisitListFilter } from './list/visit-list-filter'
import { VisitListSorting } from './list/visit-list-sorting'
import { VisitListTable } from './list/visit-list-table'

type WithUpdates<T> = T & Required<
  {
    [Key in keyof T as Key extends string
    ? `on${Capitalize<Key>}Updated`
    : never]: (value: T[Key]) => void
  }
>

type IVisitListToolbarProps = WithUpdates<{
  location?: string
  mrn?: string
  patientName?: string
  status?: string[],
  type?: string[],
}> & {
  allowedStatuses?: string[]
  currentPage: number
  itemsPerPage: number
  newRoute?: string
  onRefresh: () => void
  perPageSelect: (
    _event: React.MouseEvent | React.KeyboardEvent | MouseEvent,
    perPage: number,
  ) => void
  setPage: (
    _event: React.MouseEvent | React.KeyboardEvent | MouseEvent,
    updatedPageNumber: number,
  ) => void
  totalItemCount: number,
  onSetSort: (field: string, direction: ISortDirection) => void
  sortField?: string
  sortDirection?: ISortDirection
}

const VisitListToolbarComponent = ({
  allowedStatuses: allowedStatus,
  currentPage,
  itemsPerPage,
  newRoute,
  onRefresh,
  location,
  onLocationUpdated,
  patientName,
  onPatientNameUpdated,
  mrn,
  onMrnUpdated,
  status,
  onStatusUpdated,
  type,
  onTypeUpdated,
  perPageSelect,
  setPage,
  totalItemCount,
  onSetSort,
  sortField,
  sortDirection,
}: IVisitListToolbarProps) => {
  const onClearAllFilters = useCallback(() => {
    onLocationUpdated?.(null)
    onMrnUpdated?.(null)
    onPatientNameUpdated?.(null)
    onStatusUpdated?.([])
    onTypeUpdated?.([])
  }, [onLocationUpdated, onMrnUpdated, onPatientNameUpdated, onStatusUpdated, onTypeUpdated])

  return (
    <Toolbar
      id="toolbar-with-chip-groups"
      clearAllFilters={onClearAllFilters}
      collapseListedFiltersBreakpoint="xl"
    >
      {newRoute && (
        <ToolbarContent>
          <ToolbarItem>
            <NavLink to={newRoute}>
              <Button variant="primary">Create New Visit</Button>
            </NavLink>
          </ToolbarItem>
        </ToolbarContent>
      )}
      <ToolbarContent>
        <ToolbarItem>
          <Button variant={ButtonVariant.control} onClick={onRefresh}>
            <SyncAltIcon />
          </Button>
        </ToolbarItem>
        <ToolbarToggleGroup toggleIcon={<FilterIcon />} breakpoint="xl">
          <ToolbarGroup variant="filter-group">
            <VisitListFilter
              allowedStatuses={allowedStatus}
              location={location}
              onLocationUpdated={onLocationUpdated}
              patientName={patientName}
              onPatientNameUpdated={onPatientNameUpdated}
              mrn={mrn}
              onMrnUpdated={onMrnUpdated}
              status={status}
              onStatusUpdated={onStatusUpdated}
              type={type}
              onTypeUpdated={onTypeUpdated}
            />
          </ToolbarGroup>
        </ToolbarToggleGroup>
        <ToolbarToggleGroup toggleIcon={<SortIcon />} breakpoint="xl">
          <ToolbarGroup variant="icon-button-group">
            <VisitListSorting onSetSort={onSetSort} sortField={sortField} sortDirection={sortDirection} />
          </ToolbarGroup>
        </ToolbarToggleGroup>
        <ToolbarItem variant="pagination" alignment={{ default: 'alignRight' }}>
          <Pagination
            itemCount={totalItemCount}
            perPage={itemsPerPage}
            page={currentPage}
            onSetPage={setPage}
            widgetId="pagination-options-menu-top"
            onPerPageSelect={perPageSelect}
          />
        </ToolbarItem>
      </ToolbarContent>
    </Toolbar>
  )
}

export enum VisitListVariant {
  HidePatient = 'no_patient',
}


export interface IFilters {
  location?: string
  onLocationUpdated?: (location: string) => void

  patientName?: string
  onPatientNameUpdated?: (patientName: string) => void

  mrn?: string
  onMrnUpdated?: (mrn: string) => void

  status?: string[]
  onStatusUpdated?: (status: string[]) => void

  type?: string[]
  onTypeUpdated?: (type: string[]) => void
}

export interface IOptions {
  newRoute?: string
  variant?: VisitListVariant
  allowedStatuses?: string[]
}

interface IVisitListProps {
  filters: IFilters
  sorting: ISorting
  onRefresh: () => void
  pagination: IPagination
  options: IOptions
  visits: Array<IVisit>
}

export const VisitListComponent = ({
  filters,
  onRefresh,
  options,
  pagination,
  sorting,
  visits,
}: IVisitListProps) => {
  const dispatch = useDispatch()

  const { onSetSort, sortField, sortDirection } = sorting

  const {
    location,
    onLocationUpdated,
    patientName,
    onPatientNameUpdated,
    mrn,
    onMrnUpdated,
    status,
    onStatusUpdated,
    type,
    onTypeUpdated,
  } = filters

  const {
    pageNumber,
    onSetPage,
    onPerPageSelect,
    totalNumberOfRecords,
    recordsPerPage,
  } = pagination

  const { allowedStatuses: allowedStatus, newRoute, variant } = options

  const { isLaunchIntakeModalOpen, launchIntakeErrorMessage } = useSelector<
    AppState,
    VisitListState
  >((state) => state.visitList, shallowEqual)

  const [modalVisit, setModalVisit] = useState<IVisit | null>()

  const onClearAllFilters = useCallback(() => {
    onLocationUpdated?.(null)
    onMrnUpdated?.(null)
    onPatientNameUpdated?.(null)
    onStatusUpdated?.([])
    onTypeUpdated?.([])
  }, [onLocationUpdated, onMrnUpdated, onPatientNameUpdated, onStatusUpdated, onTypeUpdated])

  const handleSetPage = useCallback(
    (
      _event: React.MouseEvent | React.KeyboardEvent | MouseEvent,
      updatedPageNumber: number,
    ) => {
      onSetPage(updatedPageNumber)
    },
    [onSetPage],
  )

  const handlePerPageSelect = useCallback(
    (
      _event: React.MouseEvent | React.KeyboardEvent | MouseEvent,
      perPage: number,
    ) => {
      onPerPageSelect(perPage)
    },
    [onPerPageSelect],
  )

  const prepareIntake = useCallback((visit: IVisit) => {
    setModalVisit(visit)
  }, [])

  const onStartIntake = useCallback(
    (visit: IVisit) => {
      prepareIntake(visit)
      dispatch(VisitListActions.openLaunchIntakeModal())
    },
    [dispatch, prepareIntake],
  )

  const onClose = useCallback(() => {
    dispatch(VisitListActions.closeLaunchIntakeModal())
    setModalVisit(null)
  }, [dispatch])

  const startIntake = useCallback(
    (visit: IVisit, selectedInteractions: string[]) => {
      dispatch(VisitListActions.launchIntake(visit.id, selectedInteractions))
    },
    [dispatch],
  )


  const { updateStatus } = useUpdateVisitStatus()
  const { setInteraction } = useStartInteraction()
  const onStartInteraction = useCallback(
    async (visit: IVisit, selectedInteractions: string[]) => {
      await setInteraction(visit.id, selectedInteractions)
      await updateStatus(visit.id, VisitStatusId.InProgress)
      onClose()
      onRefresh()
    },
    [setInteraction, onClose, onRefresh, updateStatus],
  )

  const { handleCurrentPageChanged } = useHandleCurrentPageChanged(
    onSetPage,
    recordsPerPage,
    totalNumberOfRecords,
  )

  return (
    <>
      <VisitListToolbarComponent
        allowedStatuses={allowedStatus}
        newRoute={newRoute}
        onRefresh={onRefresh}
        location={location}
        onLocationUpdated={onLocationUpdated}
        patientName={patientName}
        onPatientNameUpdated={onPatientNameUpdated}
        mrn={mrn}
        onMrnUpdated={onMrnUpdated}
        status={status}
        onStatusUpdated={onStatusUpdated}
        perPageSelect={handlePerPageSelect}
        setPage={handleSetPage}
        itemsPerPage={recordsPerPage}
        currentPage={pageNumber}
        totalItemCount={totalNumberOfRecords}
        type={type}
        onTypeUpdated={onTypeUpdated}
        onSetSort={onSetSort}
        sortField={sortField}
        sortDirection={sortDirection}
      />
      {visits.length > 0 && (
        <>
          <VisitListTable
            onStartIntake={onStartIntake}
            variant={variant}
            visits={visits} />
          <Pagination
            itemCount={totalNumberOfRecords}
            page={pageNumber}
            perPage={recordsPerPage}
            onBlur={handleCurrentPageChanged}
            onChange={handleCurrentPageChanged}
            onPerPageSelect={handlePerPageSelect}
            onSetPage={handleSetPage}
            widgetId="pagination-options-menu-bottom"
          />
        </>
      )}
      {visits.length === 0 && (
        <React.Fragment>
          <EmptyState>
            <EmptyStateIcon icon={SearchIcon} />
            <Title headingLevel="h5" size="lg">
              No visits found
            </Title>
            {(mrn?.length > 0 ||
              location?.length > 0 ||
              status?.length > 0) && (
                <React.Fragment>
                  <EmptyStateBody>
                    No visits match this filter criteria. Remove all filters or
                    clear all filters to show results.
                  </EmptyStateBody>
                  <EmptyStateSecondaryActions>
                    <Button variant="link" onClick={onClearAllFilters}>
                      Clear all filters
                    </Button>
                  </EmptyStateSecondaryActions>
                </React.Fragment>
              )}
          </EmptyState>
        </React.Fragment>
      )}
      <LaunchIntakeModal
        onClose={onClose}
        onStartIntake={startIntake}
        onStartInteraction={onStartInteraction}
        isOpen={isLaunchIntakeModalOpen}
        visit={modalVisit}
        errorMessage={launchIntakeErrorMessage}
      />
    </>
  )
}
